import React, { useEffect, useState } from "react";
import { SortProvider, STh, useColumnSort } from "@/components/SortTh";
import { LoadingRow } from "@/components/LoadingState";
import { TrendingUp, FileSearch, Target, X, Trophy, Sparkles, PieChart as PieIcon, Download, Phone, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line, ComposedChart, PieChart, Pie, Cell } from "recharts";
import { api, API, getToken, AYLAR, formatTL, formatNum } from "@/lib/api";

export default function PrimSummaryTab({ yil, ay }) {
  const [rows, setRows] = useState([]);
  const [targets, setTargets] = useState({});
  const sumSort = useColumnSort(rows, [], {
    hedef: (r) => targets[r.personnel_id] || null,
    gerceklesme: (r) => (targets[r.personnel_id] ? r.toplam_prim / targets[r.personnel_id] : null),
  });
  const [trend, setTrend] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAll, setFilterAll] = useState(false);
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [personalTrendPerson, setPersonalTrendPerson] = useState(null);
  const [bonusPrompt, setBonusPrompt] = useState(null);
  const [ccSummary, setCCSummary] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const qs = filterAll ? "" : `?yil=${yil}&ay=${ay}`;
      const [s, t, tr, p, cc] = await Promise.all([
        api.get(`/prim/summary${qs}`),
        api.get(`/prim/targets?yil=${yil}&ay=${ay}`),
        api.get(`/prim/trend?months=6&yil=${yil}&ay=${ay}`),
        api.get("/personnel"),
        api.get(`/prim/cc-summary${qs}`),
      ]);
      setRows(s.data.rows);
      const tmap = {};
      t.data.targets.forEach((x) => { tmap[x.personnel_id] = x.aylik_hedef; });
      setTargets(tmap);
      setTrend(tr.data.rows);
      setPersonnel(p.data);
      setCCSummary(cc.data);
    } catch {
      toast.error("Özet yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [yil, ay, filterAll]);

  const totals = rows.reduce((acc, r) => {
    acc.giden_dosya += r.giden_dosya_sayisi;
    acc.giden_prim += r.giden_prim_toplami;
    acc.arastiran_dosya += r.arastiran_dosya_sayisi;
    acc.arastiran_prim += r.arastiran_prim_toplami;
    acc.toplam += r.toplam_prim;
    return acc;
  }, { giden_dosya: 0, giden_prim: 0, arastiran_dosya: 0, arastiran_prim: 0, toplam: 0 });

  const totalTrend = trend.reduce((s, t) => s + t.toplam, 0);
  const avgTrend = trend.length > 0 ? totalTrend / trend.length : 0;

  const applyBonus = async (person, hedef, achieved) => {
    const excess = achieved - hedef;
    const amount = Number(bonusPrompt.amount || excess);
    try {
      await api.post("/payroll/apply-bonus", {
        personnel_id: person.personnel_id,
        yil,
        ay,
        tutar: amount,
      });
      toast.success(`${person.ad_soyad} bordrosuna ${formatTL(amount)} başarı primi uygulandı`);
      setBonusPrompt(null);
    } catch {
      toast.error("Başarı primi uygulanamadı");
    }
  };

  return (
    <div className="space-y-4">
      {/* Trend Chart */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp size={20} /> Son 6 Ay Prim Trendi vs Hedef
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Toplam: {formatTL(totalTrend)} · Aylık ortalama: {formatTL(avgTrend)}
            </p>
          </div>
        </div>
        <div className="p-5 h-80" data-testid="prim-trend-chart">
          <TrendChart data={trend} />
        </div>
      </div>

      {/* Call Center Ödemeleri Section */}
      <CCPaymentsCard summary={ccSummary} filterAll={filterAll} yil={yil} ay={ay} />

      {/* Pie Charts: Dağılım */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DistributionCard
          title="Giden vs Araştırma Primi"
          icon={PieIcon}
          data={[
            { name: "Giden Primi", value: totals.giden_prim, fill: "#059669" },
            { name: "Araştırma Primi", value: totals.arastiran_prim, fill: "#4F46E5" },
          ]}
          testid="pie-type"
        />
        <DistributionCard
          title="Personel Bazlı Prim Payları"
          icon={PieIcon}
          data={rows
            .filter((r) => r.toplam_prim > 0)
            .map((r, i) => ({
              name: r.ad_soyad,
              value: r.toplam_prim,
              fill: PIE_COLORS[i % PIE_COLORS.length],
            }))}
          testid="pie-personnel"
        />
      </div>

      {/* Summary Table with Targets */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
              <Target size={20} /> Personel Prim & Hedef Özeti
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {filterAll ? "Tüm aylar" : `${AYLAR[ay-1].n} ${yil}`} · {rows.length} personel · Toplam {formatTL(totals.toplam)}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-1.5 bg-slate-100 rounded-lg p-1">
              <select
                data-testid="prim-summary-ay"
                value={ay}
                disabled={filterAll}
                onChange={(e) => { /* handled by App.js top selector */
                  const ev = new CustomEvent("bordromat:setAy", { detail: Number(e.target.value) });
                  window.dispatchEvent(ev);
                }}
                className="bg-white border border-slate-200 rounded-md px-2 py-1 text-xs font-semibold disabled:opacity-50"
              >
                {AYLAR.map((m) => <option key={m.v} value={m.v}>{m.n}</option>)}
              </select>
              <select
                data-testid="prim-summary-yil"
                value={yil}
                disabled={filterAll}
                onChange={(e) => {
                  const ev = new CustomEvent("bordromat:setYil", { detail: Number(e.target.value) });
                  window.dispatchEvent(ev);
                }}
                className="bg-white border border-slate-200 rounded-md px-2 py-1 text-xs font-semibold disabled:opacity-50"
              >
                {Array.from({length: 10}, (_, i) => yil - 5 + i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input data-testid="summary-filter-all" type="checkbox" checked={filterAll} onChange={(e) => setFilterAll(e.target.checked)} />
              Tüm aylar
            </label>
            <button
              data-testid="export-summary-excel"
              onClick={async () => {
                try {
                  const token = getToken();
                  const qs = filterAll ? "" : `?yil=${yil}&ay=${ay}`;
                  const res = await fetch(`${API}/prim/summary/export${qs}`, { headers: { Authorization: `Bearer ${token}` } });
                  if (!res.ok) throw new Error();
                  const blob = await res.blob();
                  const u = URL.createObjectURL(blob);
                  const a = document.createElement("a"); a.href = u;
                  a.download = `prim_ozeti_${filterAll ? "tumu" : yil + "_" + String(ay).padStart(2, "0")}.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(u);
                  toast.success("Prim özeti indirildi");
                } catch { toast.error("Excel indirilemedi"); }
              }}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-semibold"
            >
              <Download size={14} /> Excel İndir
            </button>
            <button
              data-testid="open-target-modal"
              onClick={() => setTargetModalOpen(true)}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-semibold"
            >
              <Target size={14} /> Hedef Belirle
            </button>
          </div>
        </div>

        <div className="sticky-table">
          <SortProvider value={sumSort}>
          <table className="w-full text-sm" data-testid="prim-summary-table">
            <thead>
              <tr className="bg-slate-900 text-slate-100">
                <Th k="ad_soyad">Personel</Th>
                <Th k="pozisyon">Pozisyon</Th>
                <Th center k="giden_dosya_sayisi">Giden Dosya</Th>
                <Th right k="giden_prim_toplami">Giden Primi</Th>
                <Th center k="arastiran_dosya_sayisi">Araştırma</Th>
                <Th right k="arastiran_prim_toplami">Araştırma Primi</Th>
                <Th right k="toplam_prim">Toplam Prim</Th>
                <Th right k="hedef">Hedef</Th>
                <Th k="gerceklesme">Gerçekleşme</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {loading && <LoadingRow colSpan={10} text="Prim özeti yükleniyor..." />}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10} className="py-10 text-center text-slate-400">
                  <div className="flex flex-col items-center gap-2"><FileSearch size={28} className="text-slate-300" />Bu dönemde prim kaydı bulunamadı</div>
                </td></tr>
              )}
              {sumSort.sorted.map((r) => {
                const hedef = targets[r.personnel_id] || 0;
                const pct = hedef > 0 ? (r.toplam_prim / hedef) * 100 : 0;
                let barColor = "bg-slate-300";
                let txtColor = "text-slate-500";
                if (hedef > 0) {
                  if (pct >= 100) { barColor = "bg-emerald-500"; txtColor = "text-emerald-700"; }
                  else if (pct >= 75) { barColor = "bg-slate-500"; txtColor = "text-slate-900"; }
                  else if (pct >= 50) { barColor = "bg-amber-500"; txtColor = "text-amber-700"; }
                  else { barColor = "bg-rose-500"; txtColor = "text-rose-700"; }
                }
                const excess = r.toplam_prim - hedef;
                return (
                  <tr key={r.personnel_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <button
                        data-testid={`view-trend-${r.sicil_no}`}
                        onClick={() => setPersonalTrendPerson(r)}
                        className="text-left group"
                      >
                        <div className="font-semibold text-slate-900 group-hover:text-slate-900 underline-offset-2 group-hover:underline flex items-center gap-1">
                          {r.ad_soyad}
                          <TrendingUp size={13} className="text-slate-300 group-hover:text-slate-700" />
                        </div>
                        <div className="text-xs text-slate-500 font-mono-tab">TC: {r.sicil_no}</div>
                      </button>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{r.pozisyon || "—"}</td>
                    <td className="px-5 py-3 text-center font-mono-tab text-slate-700">{r.giden_dosya_sayisi}</td>
                    <td className="px-5 py-3 text-right font-mono-tab text-emerald-700 font-semibold">{formatNum(r.giden_prim_toplami)}</td>
                    <td className="px-5 py-3 text-center font-mono-tab text-slate-700">{r.arastiran_dosya_sayisi}</td>
                    <td className="px-5 py-3 text-right font-mono-tab text-indigo-700 font-semibold">{formatNum(r.arastiran_prim_toplami)}</td>
                    <td className="px-5 py-3 text-right font-mono-tab font-bold text-slate-900">{formatTL(r.toplam_prim)}</td>
                    <td className="px-5 py-3 text-right font-mono-tab text-slate-600">
                      {hedef > 0 ? formatTL(hedef) : <span className="text-slate-400 italic text-xs">—</span>}
                    </td>
                    <td className="px-5 py-3 w-56">
                      {hedef > 0 ? (
                        <div>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className={`font-bold font-mono-tab ${txtColor}`}>%{pct.toFixed(1)}</span>
                            {pct >= 100 && <Trophy size={14} className="text-amber-500" />}
                          </div>
                          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${barColor} rounded-full transition-all`}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic">Hedef tanımlı değil</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {pct >= 100 && !filterAll && (
                        <button
                          data-testid={`apply-bonus-${r.sicil_no}`}
                          onClick={() => setBonusPrompt({ person: r, hedef, excess, amount: excess.toFixed(2) })}
                          className="inline-flex items-center gap-1.5 bg-amber-100 hover:bg-amber-200 text-amber-900 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-amber-300"
                          title={`Hedefi ${formatTL(excess)} aştı. Bordroya başarı primi olarak uygula.`}
                        >
                          <Sparkles size={12} /> Bordroya Yansıt
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="bg-slate-100 font-bold">
                  <td colSpan={2} className="px-5 py-3">TOPLAM</td>
                  <td className="px-5 py-3 text-center font-mono-tab">{totals.giden_dosya}</td>
                  <td className="px-5 py-3 text-right font-mono-tab text-emerald-700">{formatNum(totals.giden_prim)}</td>
                  <td className="px-5 py-3 text-center font-mono-tab">{totals.arastiran_dosya}</td>
                  <td className="px-5 py-3 text-right font-mono-tab text-indigo-700">{formatNum(totals.arastiran_prim)}</td>
                  <td className="px-5 py-3 text-right font-mono-tab text-slate-900">{formatTL(totals.toplam)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
          </SortProvider>
        </div>
      </div>

      {targetModalOpen && (
        <TargetModal
          personnel={personnel}
          targets={targets}
          yil={yil}
          ay={ay}
          onClose={() => setTargetModalOpen(false)}
          onSaved={() => { setTargetModalOpen(false); load(); }}
        />
      )}

      {personalTrendPerson && (
        <PersonalTrendModal
          person={personalTrendPerson}
          yil={yil}
          ay={ay}
          onClose={() => setPersonalTrendPerson(null)}
        />
      )}

      {bonusPrompt && (
        <BonusModal
          data={bonusPrompt}
          yil={yil}
          ay={ay}
          onChange={(amount) => setBonusPrompt({ ...bonusPrompt, amount })}
          onClose={() => setBonusPrompt(null)}
          onApply={() => applyBonus(bonusPrompt.person, bonusPrompt.hedef, bonusPrompt.person.toplam_prim)}
        />
      )}
    </div>
  );
}

function TrendChart({ data }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
        <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
        <YAxis
          tick={{ fill: "#64748B", fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
        />
        <Tooltip
          formatter={(v) => formatTL(v)}
          contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#F8FAFC" }}
          labelStyle={{ color: "#CBD5E1", fontWeight: 600 }}
          itemStyle={{ color: "#F8FAFC" }}
        />
        <Legend wrapperStyle={{ paddingTop: 10 }} iconType="circle" />
        <Bar dataKey="giden_prim" stackId="a" name="Giden Primi" fill="#059669" />
        <Bar dataKey="arastiran_prim" stackId="a" name="Araştırma Primi" fill="#4F46E5" radius={[6, 6, 0, 0]} />
        <Line
          type="monotone"
          dataKey="hedef"
          name="Hedef"
          stroke="#F59E0B"
          strokeWidth={3}
          strokeDasharray="6 4"
          dot={{ fill: "#F59E0B", r: 4 }}
          activeDot={{ r: 6 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function PersonalTrendModal({ person, yil, ay, onClose }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/prim/trend?months=6&yil=${yil}&ay=${ay}&personnel_id=${person.personnel_id}`)
      .then((r) => setData(r.data.rows))
      .finally(() => setLoading(false));
  }, [person, yil, ay]);

  const total = data.reduce((s, d) => s + d.toplam, 0);
  const avg = data.length > 0 ? total / data.length : 0;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-3xl" data-testid="personal-trend-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2">
              <TrendingUp size={18} /> {person.ad_soyad} · Kişisel Prim Trendi
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Son 6 ay · {person.pozisyon}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-3 gap-3 mb-5">
            <StatCard label="6 Aylık Toplam" value={formatTL(total)} tone="emerald" />
            <StatCard label="Aylık Ortalama" value={formatTL(avg)} tone="indigo" />
            <StatCard label="En Yüksek Ay" value={formatTL(Math.max(...data.map((d) => d.toplam), 0))} tone="amber" />
          </div>
          <div className="h-72">
            {loading ? (
              <div className="h-full grid place-items-center text-slate-400">Yükleniyor...</div>
            ) : (
              <TrendChart data={data} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneCls = tone === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                  tone === "indigo" ? "bg-indigo-50 border-indigo-200 text-indigo-800" :
                  "bg-amber-50 border-amber-200 text-amber-800";
  return (
    <div className={`rounded-lg p-3 border ${toneCls}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-mono-tab font-bold text-lg mt-1">{value}</div>
    </div>
  );
}

function BonusModal({ data, yil, ay, onChange, onClose, onApply }) {
  const { person, hedef, excess } = data;
  const [applying, setApplying] = useState(false);

  const doApply = async () => {
    setApplying(true);
    await onApply();
    setApplying(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md" data-testid="bonus-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Trophy size={18} className="text-amber-500" /> Başarı Primi
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-sm font-semibold text-amber-900 mb-2">
              🎉 {person.ad_soyad} hedefini aştı!
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-amber-700 font-semibold">Hedef</div>
                <div className="font-mono-tab font-bold text-slate-900">{formatTL(hedef)}</div>
              </div>
              <div>
                <div className="text-amber-700 font-semibold">Gerçekleşen</div>
                <div className="font-mono-tab font-bold text-slate-900">{formatTL(person.toplam_prim)}</div>
              </div>
              <div>
                <div className="text-amber-700 font-semibold">Fazla</div>
                <div className="font-mono-tab font-bold text-emerald-700">{formatTL(excess)}</div>
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">
              Bordroya Eklenecek Başarı Primi (₺)
            </label>
            <input
              data-testid="bonus-amount-input"
              type="number"
              step="0.01"
              value={data.amount}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono-tab text-right focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <p className="text-xs text-slate-500 mt-1.5">
              {AYLAR[ay-1].n} {yil} bordrosunun net ödemesine bu tutar eklenecek. Mevcut başarı primi varsa <strong>üzerine yazar</strong>.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">Vazgeç</button>
            <button
              data-testid="confirm-bonus-button"
              disabled={applying}
              onClick={doApply}
              className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50"
            >
              {applying ? "Uygulanıyor..." : "Bordroya Uygula"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const PIE_COLORS = ["#0F766E", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4", "#F97316", "#84CC16", "#6366F1"];

function DistributionCard({ title, icon: Icon, data, testid }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const hasData = total > 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={testid}>
      <div className="p-5 border-b border-slate-200">
        <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
          <Icon size={18} /> {title}
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">Toplam: {formatTL(total)}</p>
      </div>
      <div className="h-64 p-4">
        {!hasData ? (
          <div className="h-full grid place-items-center text-sm text-slate-400">Veri yok</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                innerRadius={40}
                paddingAngle={2}
                label={({ name, percent }) => `${name} %${(percent * 100).toFixed(0)}`}
                labelLine={false}
              >
                {data.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Pie>
              <Tooltip
                formatter={(v) => formatTL(v)}
                contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#F8FAFC" }}
                itemStyle={{ color: "#F8FAFC" }}
              />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function Th({ children, right, center, k }) {
  const align = right ? "text-right" : center ? "text-center" : "text-left";
  const cls = `px-5 py-3 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap ${align}`;
  if (k) return <STh k={k} className={cls}>{children}</STh>;
  return <th className={cls}>{children}</th>;
}

function TargetModal({ personnel, targets, onClose, onSaved, yil, ay }) {
  const [mode, setMode] = useState("default"); // "default" | "monthly"
  const [edits, setEdits] = useState(() => {
    const m = {};
    personnel.forEach((p) => { m[p.id] = targets[p.id] || ""; });
    return m;
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const monthly = mode === "monthly";
      await Promise.all(
        personnel.map((p) =>
          api.post("/prim/targets", {
            personnel_id: p.id,
            aylik_hedef: Number(edits[p.id] || 0),
            yil: monthly ? yil : null,
            ay: monthly ? ay : null,
          })
        )
      );
      toast.success(monthly
        ? `${AYLAR[ay-1].n} ${yil} için özel hedefler kaydedildi`
        : "Varsayılan hedefler kaydedildi");
      onSaved();
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-xl" data-testid="target-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><Target size={18} /> Aylık Prim Hedefleri</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="px-5 pt-5">
          <div className="inline-flex rounded-lg bg-slate-100 p-1 text-sm">
            <button
              data-testid="target-mode-default"
              onClick={() => setMode("default")}
              className={`px-3 py-1.5 rounded-md font-semibold transition-colors ${mode === "default" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            >
              Varsayılan Hedef
            </button>
            <button
              data-testid="target-mode-monthly"
              onClick={() => setMode("monthly")}
              className={`px-3 py-1.5 rounded-md font-semibold transition-colors ${mode === "monthly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            >
              Bu Ay İçin ({AYLAR[ay-1].n} {yil})
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {mode === "default"
              ? "Her ay için geçerli olan hedef. Belirli bir aya özel hedef tanımlamadıysanız bu değer kullanılır."
              : `Yalnızca ${AYLAR[ay-1].n} ${yil} için geçerli olacak özel hedef. Varsayılanı geçersiz kılar.`}
          </p>
        </div>
        <div className="p-5 space-y-3 max-h-[55vh] overflow-y-auto">
          {personnel.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
              <div>
                <div className="font-semibold text-sm text-slate-900">{p.ad_soyad}</div>
                <div className="text-xs text-slate-500 font-mono-tab">TC: {p.sicil_no}</div>
              </div>
              <input
                data-testid={`target-input-${p.sicil_no}`}
                type="number"
                step="0.01"
                value={edits[p.id]}
                onChange={(e) => setEdits({ ...edits, [p.id]: e.target.value })}
                placeholder="0.00"
                className="w-40 px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-mono-tab text-right focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          ))}
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">İptal</button>
          <button
            data-testid="save-targets-button"
            disabled={saving}
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50"
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * CC Payments Card — monthly Call Center summary with per-personnel breakdown.
 * Shows: monthly total, rate badge, approved/pending split, deep-link to Prim Hesaplama with CC filter.
 */
function CCPaymentsCard({ summary, filterAll, yil, ay }) {
  if (!summary) return null;
  const rate = Number(summary.rate || 0.07);
  const monthlyTotal = Number(summary.monthly_total || 0);
  const approved = Number(summary.approved_total || 0);
  const pending = Number(summary.pending_total || 0);
  const primCount = Number(summary.prim_count || 0);
  const persons = summary.personnel || [];

  const openPrimWithCCFilter = () => {
    try {
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem("primJumpCC", "1");
        window.dispatchEvent(new CustomEvent("bordromat:gotoTab", { detail: "prim" }));
      }
    } catch {
      // ignore
    }
  };

  const periodLabel = filterAll
    ? "Tüm Dönem"
    : `${AYLAR?.[ay - 1]?.n || ay} ${yil}`;

  if (persons.length === 0) {
    return (
      <div
        className="bg-white border border-slate-200 rounded-xl shadow-sm p-5"
        data-testid="cc-payments-card-empty"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 grid place-items-center">
              <Phone size={18} />
            </div>
            <div>
              <h3 className="font-display text-base md:text-lg font-bold text-slate-900">
                Call Center Ödemeleri
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {periodLabel} · Kural: Vekalet × %{(rate * 100).toFixed(2)} ÷ giden sayısı
              </p>
            </div>
          </div>
          <span className="text-xs text-slate-400 italic">
            Bu dönemde Call Center primi bulunmuyor
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-xl shadow-sm overflow-hidden"
      data-testid="cc-payments-card"
    >
      <div className="p-5 border-b border-amber-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500 text-white grid place-items-center shadow-sm">
            <Phone size={18} />
          </div>
          <div>
            <h3 className="font-display text-base md:text-lg font-bold text-slate-900 flex items-center gap-2">
              Call Center Ödemeleri
              <span
                className="inline-flex items-center bg-amber-200 text-amber-900 text-[10px] px-2 py-0.5 rounded-full font-bold border border-amber-300"
                title="Aktif Call Center oran kuralı"
                data-testid="cc-rate-badge"
              >
                %{(rate * 100).toFixed(rate * 100 % 1 === 0 ? 0 : 2)}
              </span>
            </h3>
            <p className="text-xs text-slate-600 mt-0.5">
              {periodLabel} · {primCount} prim satırı · {persons.length} CC personel
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="cc-jump-prim-btn"
          onClick={openPrimWithCCFilter}
          className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold shadow-sm"
          title="Prim Hesaplama tab'ına 'Yalnızca CC' filtresi aktif olarak atla"
        >
          Prim Hesaplama'da Aç <ArrowRight size={13} />
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border border-amber-200 rounded-lg p-3" data-testid="cc-kpi-total">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
              Dönem Toplam
            </div>
            <div className="text-2xl font-bold text-amber-800 mt-0.5 font-mono-tab">
              {formatTL(monthlyTotal)}
            </div>
          </div>
          <div className="bg-white border border-emerald-200 rounded-lg p-3" data-testid="cc-kpi-approved">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
              Onaylı · Bordroya Yansıdı
            </div>
            <div className="text-2xl font-bold text-emerald-800 mt-0.5 font-mono-tab">
              {formatTL(approved)}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="cc-kpi-pending">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
              Bekliyor · Onay Bekliyor
            </div>
            <div className="text-2xl font-bold text-slate-700 mt-0.5 font-mono-tab">
              {formatTL(pending)}
            </div>
          </div>
        </div>

        {/* Personel breakdown */}
        <div className="bg-white border border-amber-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-amber-100/60 border-b border-amber-200 text-[11px] font-semibold uppercase tracking-wider text-amber-900">
            Personel Bazlı Dağılım
          </div>
          <table className="w-full text-sm" data-testid="cc-breakdown-table">
            <thead className="bg-amber-50 text-[10px] uppercase tracking-wider text-amber-800">
              <tr>
                <th className="text-left px-3 py-2">Personel</th>
                <th className="text-left px-3 py-2">TC Kimlik No</th>
                <th className="text-right px-3 py-2">Dosya</th>
                <th className="text-right px-3 py-2">Onaylı</th>
                <th className="text-right px-3 py-2">Bekleyen</th>
                <th className="text-right px-3 py-2">Toplam</th>
              </tr>
            </thead>
            <tbody>
              {persons.map((p) => (
                <tr
                  key={p.personnel_id}
                  data-testid={`cc-row-${p.sicil_no}`}
                  className="border-t border-amber-100 hover:bg-amber-50/40"
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-[8px] font-bold leading-none">%</span>
                      <span className="font-semibold text-slate-900">{p.ad_soyad}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600 text-xs font-mono-tab">{p.sicil_no}</td>
                  <td className="px-3 py-2 text-right font-mono-tab text-slate-700">{p.dosya_sayisi}</td>
                  <td className="px-3 py-2 text-right font-mono-tab text-emerald-700">{formatTL(p.approved_toplam)}</td>
                  <td className="px-3 py-2 text-right font-mono-tab text-slate-500">{formatTL(p.pending_toplam)}</td>
                  <td className="px-3 py-2 text-right font-mono-tab font-bold text-amber-800">{formatTL(p.toplam)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-amber-100/60 font-bold">
              <tr>
                <td colSpan={3} className="px-3 py-2 text-amber-900 text-xs">TOPLAM</td>
                <td className="px-3 py-2 text-right font-mono-tab text-emerald-800" data-testid="cc-total-approved">{formatTL(approved)}</td>
                <td className="px-3 py-2 text-right font-mono-tab text-slate-700" data-testid="cc-total-pending">{formatTL(pending)}</td>
                <td className="px-3 py-2 text-right font-mono-tab text-amber-900" data-testid="cc-total-month">{formatTL(monthlyTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

